import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup } from '@angular/forms';
import { AnyoComponent } from 'projects/app-core/src/app/models/AnyoComponent';
import { AuthService } from 'projects/app-core/src/auth/auth.service';
import {
  LyfisService,
  UpsertPartnerProfileRequest,
  CompiledArtifactOption,
  RuleOption,
} from '../../../services/lyfis.service';
import { ToastService } from 'projects/app-core/src/app/services/toastr.service';
import { MasterdataService } from '../../../services/masterdata.service';

interface DropdownOptions {
  value: string;
  lable: string;
}

@Component({
  selector: 'app-crud-lyfis-partner-profile',
  templateUrl: './crud-lyfis-partner-profile.component.html',
  styleUrls: ['./crud-lyfis-partner-profile.component.scss'],
})
export class CrudLyfisPartnerProfileComponent extends AnyoComponent {
  form!: FormGroup;

  partnerOptions: DropdownOptions[] = [];
  diseaseOptions: DropdownOptions[] = [];
  compiledArtifactOptions: CompiledArtifactOption[] = [];
  pdhBandsRuleOptions: DropdownOptions[] = [];
  ghsBandsRuleOptions: DropdownOptions[] = [];
  pdhBandsRules: RuleOption[] = [];
  ghsBandsRules: RuleOption[] = [];
  drawerVisible = false;
  selectedRules: RuleOption[] = [];
  drawerTitle = '';

  constructor(
    auth: AuthService,
    private fb: FormBuilder,
    private lyfis: LyfisService,
    private toastService: ToastService,
    private masterdataService: MasterdataService,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    super(auth);
    this.form = this.fb.group({
      id: [undefined],
      partnerId: [undefined],
      active: [true],
      enabledDiseases: [[]],
      diseaseArtifacts: [[]],
      pdhBandsRules: [[]],
      ghsBandsRules: [[]],
    });
  }

  get diseaseArtifactRows(): Array<{ diseaseId: string; compiledArtifactId: string }> {
    return (this.form.get('diseaseArtifacts')?.value as Array<{ diseaseId: string; compiledArtifactId: string }>) || [];
  }

  addDiseaseArtifactRow(): void {
    const current = this.diseaseArtifactRows;
    this.form.get('diseaseArtifacts')?.setValue([
      ...current,
      { diseaseId: '', compiledArtifactId: '' },
    ]);
  }

  updateDiseaseArtifact(
    idx: number,
    field: 'diseaseId' | 'compiledArtifactId',
    value: string,
  ): void {
    const next = this.diseaseArtifactRows.map((row, i) =>
      i === idx ? { ...row, [field]: value } : row,
    );
    this.form.get('diseaseArtifacts')?.setValue(next);
  }

  removeDiseaseArtifact(idx: number): void {
    const next = this.diseaseArtifactRows.filter((_, i) => i !== idx);
    this.form.get('diseaseArtifacts')?.setValue(next);
  }

  artifactsForDisease(diseaseId: string): CompiledArtifactOption[] {
    return this.compiledArtifactOptions.filter((a) => a.diseaseId === diseaseId);
  }

  getArtifactDisplayLabel(artifact: CompiledArtifactOption): string {
    const excelName = artifact.fileName || artifact.scorecardId || 'Unknown Excel';
    const dateStr = artifact.compiledAt || artifact.updatedAt;
    if (dateStr) {
      const date = new Date(dateStr);
      const formattedDate = date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
      return `${excelName} (${formattedDate})`;
    }
    return excelName;
  }

  async ready() {
    if (this.user) {
      await this.loadDropdownValues();
      await this.loadPartnerOptions();
      const id = this.route.snapshot.paramMap.get('id');
      if (id) {
        this.loadProfile(id);
      }
    }
  }

  private async loadDropdownValues(): Promise<void> {
    this.lyfis.getDropdownValues().subscribe({
      next: (res) => {
        this.diseaseOptions = (res?.enabledDiseases || []).map((d) => ({
          value: d.id,
          lable: d.data,
        }));
        this.compiledArtifactOptions = res?.compiledArtifacts || [];
        this.pdhBandsRules = res?.pdhBandsRules || [];
        this.ghsBandsRules = res?.ghsBandsRules || [];
        this.pdhBandsRuleOptions = this.pdhBandsRules.map((r) => ({
          value: r.id,
          lable: r.name,
        }));
        this.ghsBandsRuleOptions = this.ghsBandsRules.map((r) => ({
          value: r.id,
          lable: r.name,
        }));
      },
      error: (err) => {
        this.toastService.showAnyoErrorToast(err.error.message);
      },
    });
  }

  private loadPartnerOptions(): void {
    this.masterdataService.partnerMasterDataList().subscribe({
      next: (res) => {
        this.partnerOptions = (res || []).map((p) => ({
          value: p.partnerId!,
          lable: p.companyName!,
        }));
      },
      error: (err) => {
        this.toastService.showAnyoErrorToast(err.error.message);
      },
    });
  }

  onSave(): void {
    const payload = this.form.value as UpsertPartnerProfileRequest;
    const payloadToSend: UpsertPartnerProfileRequest = {
      pdhBandsRules: payload.pdhBandsRules,
      ghsBandsRules: payload.ghsBandsRules,
      partnerId: payload.partnerId,
      active: payload.active,
      enabledDiseases: payload.enabledDiseases,
      diseaseArtifacts: payload.diseaseArtifacts,
    };
    if (payload.id) {
      payloadToSend.id = payload.id;
    }
    this.lyfis.upsertPartnerProfile(payloadToSend).subscribe({
      next: (res) => {
        this.toastService.showSuccess('Partner profile saved');
        this.form.patchValue({
          id: res?.id,
          partnerId: res?.partnerId ?? undefined,
          active: res?.active ?? true,
          enabledDiseases: res?.enabledDiseases || [],
          diseaseArtifacts: res?.diseaseArtifacts || [],
          pdhBandsRules: res?.pdhBandsRules || [],
          ghsBandsRules: res?.ghsBandsRules || [],
        });
        this.router.navigate(['/lyfis'] as string[]);
      },
      error: (err) => {
        this.toastService.showAnyoErrorToast(
          err?.error?.message ||
          err?.message ||
          'Failed to save partner profile',
        );
      },
    });
  }

  private loadProfile(id: string): void {
    this.lyfis.getPartnerProfile(id).subscribe({
      next: (res) => {
        this.form.patchValue({
          id: res?.id ?? id,
          partnerId: res?.partnerId ?? undefined,
          active: res?.active ?? true,
          enabledDiseases: res?.enabledDiseases || [],
          diseaseArtifacts: res?.diseaseArtifacts || [],
          pdhBandsRules: res?.pdhBandsRules || [],
          ghsBandsRules: res?.ghsBandsRules || [],
        });
      },
      error: (err) => {
        this.toastService.showAnyoErrorToast(
          err?.error?.message ||
          err?.message ||
          'Failed to load partner profile',
        );
      },
    });
  }

  openRuleViewer(ruleType: 'pdhBandsRules' | 'ghsBandsRules'): void {
    if (ruleType === 'pdhBandsRules') {
      this.selectedRules = this.pdhBandsRules;
      this.drawerTitle = 'PDH Bands Rules';
    } else {
      this.selectedRules = this.ghsBandsRules;
      this.drawerTitle = 'GHS Bands Rules';
    }
    this.drawerVisible = true;
  }

  closeDrawer(): void {
    this.drawerVisible = false;
    this.selectedRules = [];
  }
}
